import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import API from '../api';
import LanguageSwitcher from './LanguageSwitcher';
import { useAppModal } from '../hooks/useAppModal';

function MaintenancePage() {
  return (
    <div style={{
      backgroundColor: '#05006C',
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      padding: '24px',
      fontFamily: 'sans-serif',
    }}>
      {/* Sfondo con cerchi decorativi */}
      <div style={{ position: 'fixed', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{
          position: 'absolute', width: 500, height: 500, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,255,136,0.06) 0%, transparent 70%)',
          top: '-100px', right: '-100px',
        }} />
        <div style={{
          position: 'absolute', width: 400, height: 400, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,170,255,0.05) 0%, transparent 70%)',
          bottom: '-80px', left: '-80px',
        }} />
      </div>

      {/* Logo */}
      <img
        src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`}
        alt="Kuesto Logo"
        style={{ width: 130, marginBottom: 48, position: 'relative', zIndex: 1 }}
      />

      {/* Card manutenzione */}
      <div style={{
        position: 'relative', zIndex: 1,
        backgroundColor: 'rgba(17,17,40,0.92)',
        border: '1px solid rgba(0,255,136,0.2)',
        borderRadius: 20,
        padding: '48px 40px',
        maxWidth: 480,
        width: '100%',
        textAlign: 'center',
        boxShadow: '0 8px 40px rgba(0,0,0,0.5), 0 0 80px rgba(0,255,136,0.04)',
      }}>
        {/* Icona animata */}
        <div style={{
          width: 80, height: 80, borderRadius: '50%', margin: '0 auto 28px',
          backgroundColor: 'rgba(0,255,136,0.08)',
          border: '2px solid rgba(0,255,136,0.25)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#00ff88" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
          </svg>
        </div>

        <h2 style={{
          color: '#ffffff', fontSize: 22, fontWeight: 700, marginBottom: 16, lineHeight: 1.3,
        }}>
          Sistema temporaneamente non disponibile
        </h2>

        <p style={{
          color: '#aab', fontSize: 15, lineHeight: 1.7, marginBottom: 28,
        }}>
          Il sistema non è attivo al momento poiché stiamo lavorando per il miglioramento di Kuesto.
        </p>

        {/* Divisore decorativo */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28,
        }}>
          <div style={{ flex: 1, height: 1, backgroundColor: 'rgba(255,255,255,0.07)' }} />
          <div style={{
            display: 'flex', gap: 5,
          }}>
            {[0, 1, 2].map(i => (
              <div key={i} style={{
                width: 6, height: 6, borderRadius: '50%',
                backgroundColor: '#00ff88',
                opacity: 0.4 + i * 0.2,
              }} />
            ))}
          </div>
          <div style={{ flex: 1, height: 1, backgroundColor: 'rgba(255,255,255,0.07)' }} />
        </div>

        <p style={{ color: '#667', fontSize: 13, margin: 0 }}>
          Ci scusiamo per il disagio. Torneremo operativi a breve.
        </p>
      </div>

      {/* Footer */}
      <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: 12, marginTop: 32, position: 'relative', zIndex: 1 }}>
        © {new Date().getFullYear()} Kuesto — Gestione algoritmica del portafoglio
      </p>
    </div>
  );
}

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [user, setUser] = useState(null);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
//  const [isFirstLogin, setIsFirstLogin] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { modal, showModal } = useAppModal();

  useEffect(() => {
    API.get('/admin/maintenance')
      .then(r => setMaintenanceMode(r.data.maintenance))
      .catch(() => {});
  }, []);

  // Redirect automatico se è il primo login
  useEffect(() => {
    if (user && user.first_login) {
      navigate('/kyc/email-verification');
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await API.post('/login', { email, password });
      const data = res.data;

      // Salva i dati utente
      localStorage.setItem('userId', data.user_id);

      // Lingua: priorità a quella scelta localmente (es. sulla pagina di login),
      // altrimenti usa quella salvata nel profilo sul server.
      const localLang = localStorage.getItem('language');
      if (localLang) {
        // Allinea il server alla preferenza locale se diversa
        if (data.language !== localLang) {
          try { await API.put(`/users/${data.user_id}/language`, { language: localLang }); } catch {}
        }
        await i18n.changeLanguage(localLang);
      } else if (data.language) {
        localStorage.setItem('language', data.language);
        await i18n.changeLanguage(data.language);
      }

      setUser(data);

      if (data.first_login) {
        navigate('/kyc/email-verification');
      } else {
        navigate('/portfolio');
      }
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.code === 'user_blocked') {
        showModal({ type: 'error', title: t('login.blocked_title'), message: t('login.blocked_message') });
      } else {
        showModal({ type: 'error', title: t('login.failed') });
      }
    }
  };

  if (maintenanceMode) {
    return <MaintenancePage />;
  }

  return (
    <>
    {modal}
    <div
      style={{
        backgroundColor: '#05006C',
        color: 'white',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      {/* Logo */}
      <img
        src={`${process.env.PUBLIC_URL}/Logo_Kuesto.png`}
        alt="Kuesto Logo"
        style={{ width: '150px', marginBottom: '20px' }}
      />

      {/* Login Card */}
      <div
        className="card"
        style={{
          width: '100%',
          maxWidth: '400px',
          backgroundColor: '#1a1a1a',
          color: 'white',
          borderRadius: '10px',
          padding: '20px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.7)'
        }}
      >
        <LanguageSwitcher />
        <h3
          className="text-center"
          style={{ marginBottom: '20px', color: '#00ff88' }}
        >
          {t('login.title')}
        </h3>
        <form onSubmit={handleLogin}>
          <div className="form-group mb-3">
            <label htmlFor="email" style={{ color: '#ccc' }}>
              {t('login.email')}:
            </label>
            <input
              type="email"
              className="form-control"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555',
                padding: '10px',
                borderRadius: '5px'
              }}
            />
          </div>
          <div className="form-group mb-4">
            <label htmlFor="password" style={{ color: '#ccc' }}>
              {t('login.password')}:
            </label>
            <input
              type="password"
              className="form-control"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              style={{
                backgroundColor: '#333',
                color: 'white',
                border: '1px solid #555',
                padding: '10px',
                borderRadius: '5px'
              }}
            />
          </div>
          <button
            type="submit"
            className="btn btn-primary w-100"
            style={{
              backgroundColor: '#00ff88',
              border: 'none',
              padding: '10px',
              fontSize: '18px',
              fontWeight: 'bold',
              borderRadius: '5px',
              color: 'black',
              cursor: 'pointer'
            }}
          >
            {t('login.button')}
          </button>
        </form>
        <div className="text-center mt-3">
          <p style={{ color: '#ccc' }}>
            {t('login.no_account')}{' '}
            <a
              href="/signup"
              style={{ color: '#00aaff', textDecoration: 'none' }}
            >
              {t('login.signup_link')}
            </a>
          </p>
        </div>
      </div>
    </div>
    </>
  );
}

export default Login;
